import type { JSX, ReactNode } from 'react';
import { cx } from './cx';

/**
 * The small selection controls: Material's
 * `SingleChoiceSegmentedButtonRow`/`SegmentedButton` pair, and the read-only row
 * that stands in for a setting this app does not own.
 *
 * Source: the segmented filter row in `android/.../ui/result/ResultScreen.kt`.
 * Android's `FilterChip` has no port — the chips it uses in
 * `ui/settings/SettingsScreen.kt` pick an auto-run interval, which is a setting
 * this app does not have (see {@link ReadOnlyRow}), so a `FilterChip` here was
 * an exported component with no caller.
 *
 * ### Why these are `aria-pressed` buttons and not radios
 *
 * Compose gives a `SegmentedButton` `Role.RadioButton`, and the literal port
 * would be `role="radio"` inside a `role="radiogroup"`. That role carries a
 * keyboard contract on the web that Compose's does not: a radiogroup is
 * expected to be one tab stop with arrow keys moving the selection, which means
 * roving `tabindex` and a key handler. A WebClip on a field iPhone has no
 * keyboard and no arrow keys, so implementing that would be dead code, and
 * *declaring* the role without implementing it leaves an assistive-technology
 * user in a group they cannot move through. Toggle buttons are honest about
 * what these are: three separately reachable controls, each announcing whether
 * it is on.
 */

/**
 * The container for a run of [SegmentedButton]s. Segments share their edges, so
 * the row has to be a single flex line with no gap — the collapsed borders are
 * what make three controls read as one.
 *
 * `label` becomes the group's accessible name. Optional because a row that sits
 * directly under a screen title is already named by it.
 */
export function SegmentedButtonRow({
  children,
  label,
  className,
}: {
  children: ReactNode;
  label?: string;
  className?: string;
}): JSX.Element {
  return (
    <div className={cx('segmented-row', className)} role="group" aria-label={label}>
      {children}
    </div>
  );
}

/**
 * One segment. The label wraps to two lines rather than truncating: three
 * segments share the screen width and each label carries a count, so
 * "Fora do ar 12" losing its number is worse than losing a line of height.
 * Android says the same thing in `ResultScreen.kt` with `maxLines = 2`.
 */
export function SegmentedButton({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick(): void;
}): JSX.Element {
  return (
    <button
      type="button"
      className={cx('segmented-btn', selected && 'segmented-btn--selected')}
      onClick={onClick}
      aria-pressed={selected}
    >
      <span className="segmented-btn__label t-label-large clamp-2">{label}</span>
    </button>
  );
}

/**
 * A stacked label and description inside a module.
 *
 * This is what **replaces** Android's auto-run switch and its interval chips.
 * Not a disabled version of them: this app has no settings of its own and
 * always follows the newest value the server sent (SPEC §9, CLAUDE.md), so a
 * greyed-out switch would imply there is something here that could be turned on
 * if only you had permission. A row of text says what is true instead.
 *
 * Android needs a config-conflict rule because it has hand-set values to
 * protect. There are none here, so there is no rule to get wrong — and no
 * control to render.
 */
export function ReadOnlyRow({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="read-only-row">
      <span className="read-only-row__label t-body-large">{label}</span>
      <span className="read-only-row__value t-body-small">{value}</span>
    </div>
  );
}
